import { type Except, notEmpty } from '@festina-life/core'
import cx from 'classnames'
import { type JSX, type MouseEvent, useEffect, useMemo } from 'react'

import { EmptyState } from '../../feedback/emptyState/EmptyState'
import { useStableObjectKey } from '../../hooks/useStableObjectKey'
import { useTranslate } from '../../hooks/useTranslate'
import {
  Table,
  type TableProps,
  TBody,
  TFoot,
  Th,
  THead,
  Tr,
} from '../table/Table'
import type { RowAction } from '../types'
import type { Column, ColumnId } from './createColumnDefiner'
import { SortableTableDataCell } from './SortableTableDataCell'
import { SortableTableHeader } from './SortableTableHeader'
import { SortableTableRow } from './SortableTableRow'
import { useFilterState } from './useFilterState'
import { useSortingState } from './useSortingState'

const rightStickyCellStyles = cx(
  'sticky [[data-amaze-cardbody]>table_&]:-right-1.5 right-0 z-1',
  'before:content-[""] before:absolute before:-left-px before:w-px before:h-full before:top-0 before:z-2 before:bg-slate-200 dark:before:bg-slate-700',
)

type Props<T> = {
  data: Required<T[]>
  footerData?: T[]
  columns: Record<ColumnId, Column<T>>
  searchCriteria?: string
  rowOnClick?: (x: T, e: MouseEvent<HTMLTableRowElement>) => void
  rowTo?: (x: T) => string | undefined
  rowIsSelected?: (x: T) => boolean
  rowActions?: (x: T) => RowAction[]
  rowClassName?: (x: T) => JSX.IntrinsicElements['tr']['className']
  onPresentDataChange?: (data: T[]) => void
  emptyOptions?: {
    itemName?: string
    reason?: string
  }
  /**
   * When we use async API requests one may not want to have both filtering capabilities enabled.
   */
  shouldSkipLocalSorting?: boolean
} & Except<TableProps, 'title' | 'children'>

export function SortableTable<T extends WeakKey>({
  searchCriteria,
  data,
  footerData,
  columns,
  rowOnClick,
  rowTo,
  rowIsSelected,
  rowActions,
  rowClassName,
  onPresentDataChange,
  emptyOptions,
  shouldSkipLocalSorting = false,
  ...restProps
}: Props<T>) {
  const translate = useTranslate()

  const rowKey = useStableObjectKey<T>()

  const {
    extractionMirrors,
    getSortedRows,
    getSortingState,
    cycleColumnSorting,
  } = useSortingState<T>({
    columns,
    rows: data,
    shouldSkipLocalSorting,
  })

  const { getFilteredRows, getFilterState, setFilterState } = useFilterState<T>(
    {
      columns,
      rows: data,
      globalSearch: searchCriteria ?? '',
    },
  )

  const rows: T[] = useMemo(
    () =>
      // Following https://tanstack.com/table/latest/docs/guide/row-models#the-order-of-row-model-execution,
      // filter-then-sort
      getSortedRows(getFilteredRows(data.filter(notEmpty))),
    [getSortedRows, getFilteredRows, data],
  )

  useEffect(() => {
    onPresentDataChange?.(rows)
  }, [rows, onPresentDataChange])

  return (
    <>
      <Table {...restProps}>
        {rows.length === 0 && (
          <caption className="caption-bottom">
            <EmptyState
              itemName={emptyOptions?.itemName}
              reason={emptyOptions?.reason}
              variant="tableCaption"
            />
          </caption>
        )}
        <THead>
          <Tr>
            {Object.entries(columns).map(([columnId, column]) => (
              <SortableTableHeader
                key={columnId}
                column={column}
                sortingState={getSortingState(columnId)}
                filterState={getFilterState(columnId)}
                onClick={
                  column.sortable !== false
                    ? (e) =>
                        cycleColumnSorting({
                          columnId,
                          // metaKey for command key on Mac https://stackoverflow.com/a/3922353
                          isAdditive: e.ctrlKey || e.metaKey,
                        })
                    : undefined
                }
                onStateChanged={(state) => setFilterState(columnId, state)}
              />
            ))}
            {rowActions && (
              <Th className={rightStickyCellStyles}>{translate('actions')}</Th>
            )}
          </Tr>
        </THead>
        <TBody>
          {rows.map((row) => (
            <SortableTableRow
              key={rowKey(row)}
              row={row}
              columns={columns}
              rowOnClick={rowOnClick}
              rowActions={rowActions}
              to={rowTo?.(row)}
              isSelected={rowIsSelected?.(row)}
              className={rowClassName?.(row)}
              rightStickyCellStyles={rightStickyCellStyles}
            />
          ))}
        </TBody>
        {footerData && (
          <TFoot>
            {footerData.map((row) => (
              <Tr key={rowKey(row)}>
                {Object.entries(columns).map(([columnId, column]) => (
                  <SortableTableDataCell
                    key={columnId}
                    column={column}
                    row={row}
                    isHeader
                  />
                ))}
                {rowActions && <Th />}
              </Tr>
            ))}
          </TFoot>
        )}
      </Table>
      {extractionMirrors}
    </>
  )
}
